import axios from 'axios';
import { useSelector } from 'react-redux';
import { apiConstants } from '../common/constants';
import { decodeAuthToken } from '../helpers/UtilityHelper';
import { login, setSelectedMerchantOid } from '../redux/userSlice';
import { commonRTK } from '../redux/commonRTK';
import { commonRTKPersist } from '../redux/commonRTKPersist';

const HEAVY_MERCHANT_FIELDS = [
  'mappedOrderRule', 'wallet', 'pickup_details', 'bank_details',
  'alertSubscription', 'gstDetails', 'merchantWebHooks', 'activeSlas', 'alertSubscriptionB2B',
];

// Same mechanism as the Proship-admin merchant picker: one token for the whole session,
// never reissued, no dedicated switch/validation API — same as admin, which calls none either.
// Switching means "remember this OID and re-declare it on every call" via the X-Merchant-Oid
// header (see the request interceptor in src/routes/PrivateRoute.js); this merchant-details
// fetch carries that header itself, so a rejected switch surfaces right here as a 403 — no
// separate pre-flight call needed. Used by the post-login full-page picker (SelectLogin.js).
export async function applyMerchantSelection(dispatch, userInfo, targetMerchantOid) {
  const {merchantOid: homeMerchantOid} = decodeAuthToken(userInfo.accessToken);
  // Sending the header for the home merchant is redundant — absent means home already.
  const effectiveOid = targetMerchantOid === homeMerchantOid ? null : targetMerchantOid;

  // Commit the target BEFORE the fetch: the request interceptor (PrivateRoute.js) stamps
  // X-Merchant-Oid from state.selectedMerchantOid on every call and would otherwise clobber a
  // header we set here with a stale value left over from a previous switch or a persisted
  // session — which scopes merchant/search to the wrong merchant and returns an empty result.
  dispatch(setSelectedMerchantOid(effectiveOid));

  const merchantRes = await axios.post(
    `${apiConstants.BASE_LOCAL}${apiConstants.GET_MERCHANT_SEARCH}`,
    {
      offset: 0,
      limit: 1,
      andfilter: [{field: 'id', operator: 'eq', value: targetMerchantOid}],
      excludes: 'mappedOrderRule,sla',
    },
    {
      headers: {
        Authorization: `Bearer ${userInfo.accessToken}`,
        ...(effectiveOid ? {'X-Merchant-Oid': effectiveOid} : {}),
      },
    }
  );
  const merchantData = merchantRes?.data?.data?.[0];
  if (merchantData) {
    HEAVY_MERCHANT_FIELDS.forEach((field) => delete merchantData[field]);
  }

  // Merchant-scoped caches (orders, wallet, dashboards, etc.) must not leak across a switch.
  dispatch(commonRTK.util.resetApiState());
  dispatch(commonRTKPersist.util.resetApiState());
  // Guard against a blank merchant: every feature page reads getMerchant().id to build its own
  // API calls, so an undefined merchant here makes all of them fire with an empty merchant id.
  dispatch(login({...userInfo, merchant: merchantData || userInfo.merchant}));
}

export default function useMerchantSwitcher() {
  const accessToken = useSelector((state) => state.user.userInfo.accessToken);
  const { switchableMerchantOids } = decodeAuthToken(accessToken);
  const showSwitcher = Array.isArray(switchableMerchantOids) && switchableMerchantOids.length > 0;

  return { showSwitcher };
}
